import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import { hasTriggeredExtraClickFromKeyUpEvent } from 'src/apps/LearningSession/utils/hasTriggeredExtraClickFromKeyUpEvent';
import { LearningSessionAction } from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { ButtonColor } from './TestButton';
import { Direction, getNextFocusedButtonColumnIndex, getNextFocusedButtonGridIndex } from './utils';
import * as S from './styles';

type MCResponseProps = {
  choices: string[];
  correctChoice: string;
  type: 'Text' | 'Image';
  showFeedback: boolean;
  largeScreenLayout: 'grid' | 'column';
  learningSessionDispatch: (action: LearningSessionAction) => void;
  showBigger: boolean;
  showCorrectAnswer: boolean;
  onVideo?: boolean;
};

export const MCResponse: FC<MCResponseProps> = ({
  choices,
  correctChoice,
  showFeedback,
  type,
  largeScreenLayout,
  learningSessionDispatch,
  showBigger,
  showCorrectAnswer,
  onVideo = false,
}) => {
  /* istanbul ignore next: internal error */
  if (choices.length < 4 || choices.length > 8)
    throw Error(`This component does not support ${choices.length} choices`);

  // We assume that the user has not focused anywhere using
  // non-arrow key methods (e.g. tabbing, clicking) which
  // allows us to set the initial currentlyFocused value to null
  //
  // Note that if the user manually clicks/otherwise navigates
  // to focus on a button we do not listen for this event
  // nor update the component state. This is because listening
  // for focus events such as these is surprisingly hard in
  // React and a user switching between different focus methods
  // is considered unlikely.
  const [focusRefIndex, setFocusRefIndex] = useState<number | null>(null);

  const [userAnswer, setUserAnswer] = useState<string | null>(null);

  const answerOneRef = useRef<HTMLButtonElement>(null);
  const answerTwoRef = useRef<HTMLButtonElement>(null);
  const answerThreeRef = useRef<HTMLButtonElement>(null);
  const answerFourRef = useRef<HTMLButtonElement>(null);
  const answerFiveRef = useRef<HTMLButtonElement>(null);
  const answerSixRef = useRef<HTMLButtonElement>(null);
  const answerSevenRef = useRef<HTMLButtonElement>(null);
  const answerEightRef = useRef<HTMLButtonElement>(null);

  const refs = useMemo(
    () => [
      answerOneRef,
      answerTwoRef,
      answerThreeRef,
      answerFourRef,
      answerFiveRef,
      answerSixRef,
      answerSevenRef,
      answerEightRef,
    ],
    [],
  );

  /**
   * Updates the component state to keep track of which
   * element is focused and focuses that element accordingly.
   */
  const focusNext = useCallback(
    (direction: Direction) => {
      const nextFocusRefIndex =
        largeScreenLayout === 'grid'
          ? // keyboard navigation for grid layout
            getNextFocusedButtonGridIndex(direction, focusRefIndex, choices.length)
          : /* istanbul ignore next: untested branch of code, please test */
            getNextFocusedButtonColumnIndex(direction, focusRefIndex, choices.length);

      setFocusRefIndex(nextFocusRefIndex);
      const elToFocus = refs[nextFocusRefIndex];

      elToFocus.current && elToFocus.current.focus();
    },
    [largeScreenLayout, focusRefIndex, choices.length, refs],
  );

  useKeyboardShortcut(['up', 'k'], () => focusNext('up'));
  useKeyboardShortcut(['right', 'l'], () => focusNext('right'));
  useKeyboardShortcut(['down', 'j'], () => focusNext('down'));
  useKeyboardShortcut(['left', 'h'], () => focusNext('left'));

  const onAnswered = useCallback(
    (answer: string): void => {
      learningSessionDispatch({ type: 'ANSWER_TEST', answer });
      setUserAnswer(answer);
    },
    [learningSessionDispatch, setUserAnswer],
  );

  const onClick = (answer: string, event?: MouseEvent<HTMLButtonElement>) => {
    if (hasTriggeredExtraClickFromKeyUpEvent(event)) return;
    onAnswered(answer);
  };

  const onKeyUp = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, answer: string) => {
      if (event?.key === 'Enter' || event?.key === ' ') {
        onAnswered(answer);
      }
    },
    [onAnswered],
  );

  // reset the userAnswer when showFeedback value is reset
  useEffect(() => {
    if (!showFeedback) {
      setUserAnswer(null);
    }
  }, [showFeedback, setUserAnswer]);

  return (
    <S.ButtonContainer $largeScreenLayout={largeScreenLayout} $onVideo={onVideo}>
      {choices.map((choice, i) => {
        const isCorrectAnswer = correctChoice === choice;
        const isUserAnswer = userAnswer === choice;
        const buttonColor =
          isCorrectAnswer && (showCorrectAnswer || showFeedback)
            ? ButtonColor.GREEN
            : isUserAnswer && showFeedback
              ? ButtonColor.RED
              : ButtonColor.GREY;

        return (
          <S.Button
            /* eslint-disable-next-line react/no-array-index-key */
            key={`${choice}-${i}-${correctChoice}`}
            data-testid={`mcResponse-choice${i + 1}`}
            positionLabel={i + 1}
            ref={refs[i]}
            buttonColor={buttonColor}
            onClick={(event?: MouseEvent<HTMLButtonElement>) => onClick(choice, event)}
            onKeyUp={(event: KeyboardEvent<HTMLButtonElement>) => onKeyUp(event, choice)}
            disabled={showFeedback}
            highlight={showFeedback}
            onVideo={onVideo}
            $onVideo={onVideo}
            $showBigger={showBigger}
          >
            {type === 'Text' ? (
              <span dir="auto">{choice}</span>
            ) : (
              <S.Image src={choice} alt="" data-testid="MCResponse-image" />
            )}
          </S.Button>
        );
      })}
    </S.ButtonContainer>
  );
};
